import { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { FiMail } from 'react-icons/fi';

import Button from '../Button';
import Notification from '../Notification';
import ApolloAuth from '../../services/apolloAuth';

import {
  Headline,
  Content,
  Email,
  Hint,
  ButtonsContainer,
  modalStyles,
} from './styles';

const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  email: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

const defaultProps = {
  email: '',
};

const modalCloseTimeout = 500;
// Conforto, não proteção: quem abrir o console passa por cima. Quem conta é o
// limite do Apolo, de 5 pedidos por hora. A espera aqui existe para a pessoa
// dar tempo ao e-mail chegar em vez de insistir e gastar a cota à toa.
const RESEND_COOLDOWN_SECONDS = 120;

const UnverifiedAccountModal = ({ isOpen, email, onClose }) => {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [isSending, setSending] = useState(false);
  const intervalRef = useRef(null);

  function stopCountdown() {
    if (!intervalRef.current) return;

    clearInterval(intervalRef.current);
    intervalRef.current = null;
  }

  function startCountdown() {
    setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    stopCountdown();

    intervalRef.current = setInterval(() => {
      setSecondsLeft(current => {
        if (current <= 1) {
          stopCountdown();
          return 0;
        }

        return current - 1;
      });
    }, 1000);
  }

  // A contagem não sobrevive ao fechamento: reabrir o modal é um caminho novo,
  // e o limite de verdade continua sendo o do servidor.
  useEffect(() => {
    if (isOpen) return undefined;

    stopCountdown();
    setSecondsLeft(0);
    return undefined;
  }, [isOpen]);

  useEffect(() => stopCountdown, []);

  async function handleResend() {
    try {
      setSending(true);
      await ApolloAuth.resendVerification({ email });

      Notification({
        message: 'Link reenviado. Confira sua caixa de entrada.',
        type: Notification.types.SUCCESS,
      });
      startCountdown();
    } catch (err) {
      const message =
        err.response?.data?.error ||
        'Não foi possível reenviar agora. Tente novamente em instantes.';

      Notification({ message, type: Notification.types.ERROR });

      // 429 é o próprio limite de reenvio: segurar o botão evita insistir.
      if (err.response?.status === 429) startCountdown();
    } finally {
      setSending(false);
    }
  }

  const isWaiting = secondsLeft > 0;
  // "Reenviar em 118s" faz a pessoa converter de cabeça; com dois minutos de
  // espera, minuto e segundo lê melhor.
  const timeLeft =
    secondsLeft >= 60
      ? `${Math.floor(secondsLeft / 60)}min ${String(secondsLeft % 60).padStart(
          2,
          '0'
        )}s`
      : `${secondsLeft}s`;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={modalStyles}
      closeTimeoutMS={modalCloseTimeout}
    >
      <Content>
        <FiMail color="var(--primary)" size={64} />
        <Headline>Confirme seu e-mail</Headline>
        <p>
          Sua conta ainda não foi confirmada. Enviamos um link para{' '}
          <Email>{email}</Email> — abra o link para liberar o acesso.
        </p>
        <Hint>
          Não chegou? O e-mail pode levar alguns instantes. Vale conferir a
          caixa de spam antes de pedir outro.
        </Hint>
      </Content>
      <ButtonsContainer>
        <Button text="Fechar" onClick={onClose} />
        <Button
          primary
          text={isWaiting ? `Reenviar em ${timeLeft}` : 'Reenviar link'}
          loadingText="Reenviando..."
          isLoading={isSending}
          isDisabled={isWaiting}
          onClick={handleResend}
        />
      </ButtonsContainer>
    </Modal>
  );
};

UnverifiedAccountModal.propTypes = propTypes;
UnverifiedAccountModal.defaultProps = defaultProps;

UnverifiedAccountModal.closeTimeout = modalCloseTimeout;

export default UnverifiedAccountModal;
